import { OptionsDeleteIcon } from 'assets/icons/OptionsDeleteIcon'
import { Badge } from 'components/Badge/Badge'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { Input } from 'components/Input/Input'
import { AddPhaseSection, NotYetAdded, SandboxPreview } from 'pages/Operations/Operations.styled'
import { useState } from 'react'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IAddOperationPlanTab, IPhaseState, ITaskCycleBindingConfigState, IProcessBindingConfigState, IWorkflowBindingConfigState } from 'types/operationPlans.interface'
import { ISelectValue } from 'constants/globalTypes'
import { toast } from 'react-toastify'
import { advanceRuleList, executableTypeList } from '../../plansData'
import { CycleTemplateType } from './PhasesExecutionTypes/CycleTemplateType'
import { ProcessTemplateType } from './PhasesExecutionTypes/ProcessTemplateType'
import { WorkflowTemplateType } from './PhasesExecutionTypes/WorkflowTemplateType'

export const Phases = ({ setStep, setState, state }: IAddOperationPlanTab) => {
    const [showAddPhase, setShowAddPhase] = useState(false)
    const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null)
    const [addingPhase, setAddingPhase] = useState<IPhaseState<IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState>>({
        id: null,
        name: null,
        sequence: null,
        executableType: null,
        advanceRule: null,
        workflowTemplate: null,
        processTemplate: null,
        taskCycleTemplate: null,
        bindingConfig: null,
    })
    const resetAddingPhase = () => {
        setAddingPhase({
            id: null,
            name: null,
            sequence: null,
            executableType: null,
            advanceRule: null,
            workflowTemplate: null,
            processTemplate: null,
            taskCycleTemplate: null,
            bindingConfig: null,
        })
        setEditingPhaseId(null)
    }
    const handleNext = () => setStep(5)
    const handleBack = () => setStep(3)

    const handleDeletePhase = (id: number) => {
        setState((prev) => ({ ...prev, phases: prev.phases.filter((phase) => phase.id !== id) }))
        if (editingPhaseId === id) {
            resetAddingPhase()
            setShowAddPhase(false)
        }
    }

    const handleEditPhase = (phase: IPhaseState<IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState>) => {
        setAddingPhase({ ...phase })
        setEditingPhaseId(phase.id)
        setShowAddPhase(true)
    }

    const columns: IHonestTableColumn<IPhaseState<IWorkflowBindingConfigState | IProcessBindingConfigState | ITaskCycleBindingConfigState>>[] = [
        {
            id: 'name',
            header: 'Name',
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <Flex column gap='0.2rem' >
                        <BodyText weight='600'>{item.name}</BodyText>
                        <OtherText grey >
                            {item.sequence?.toString()}
                        </OtherText>
                    </Flex>
                </Cell>
            ),
        },
        {
            id: 'spacer',
            header: '',
            fill: true,
            cell: () => null,
        },
        {
            id: 'type',
            header: 'Type',
            align: 'right',
            cell: (item) => (
                <RightCell>
                    <Badge text={item.executableType?.label} orange />
                </RightCell>
            ),
        },
        {
            id: 'rule',
            header: 'Rule',
            align: 'right',
            cell: (item) => (
                <RightCell>
                    <BodyText>{item.advanceRule?.label}</BodyText>
                </RightCell>
            ),
        },
        {
            id: 'delete',
            header: '',
            align: 'right',
            minWidth: '3rem',
            cell: (item) => (
                <RightCell>
                    <CursorPointer onClick={(e) => {
                        e.stopPropagation()
                        handleDeletePhase(item.id)
                    }} >
                        <OptionsDeleteIcon />
                    </CursorPointer>
                </RightCell>
            ),
        },
    ]

    const handleSelect = (v: ISelectValue, n: string) => {
        setAddingPhase((prev) => {
            if (n === 'executableType') {
                return {
                    ...prev,
                    executableType: v,
                    taskCycleTemplate: null,
                    workflowTemplate: null,
                    processTemplate: null,
                    bindingConfig: null,
                }
            }
            return { ...prev, [n]: v }
        })
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name, type } = e.target
        setAddingPhase((prev) => ({ ...prev, [name]: type === 'number' ? Number(value) : value }))
    }

    const handleAddPhase = () => {

        if (!addingPhase.name || !addingPhase.sequence || !addingPhase.executableType || !addingPhase.advanceRule) {
            toast.error('Please fill all required fields')
            return
        }

        if (editingPhaseId) {
            setState((prev) => ({
                ...prev,
                phases: prev.phases.map((phase) => phase.id === editingPhaseId ? { ...addingPhase, id: editingPhaseId } : phase),
            }))
        } else {
            const id = Date.now()
            setState((prev) => ({ ...prev, phases: [...prev.phases, { ...addingPhase, id }] }))
        }
        resetAddingPhase()
        setShowAddPhase(false)
    }

    const handleCloseAddPhase = () => {
        resetAddingPhase()
        setShowAddPhase(false)
    }

    const handleOpenAddPhase = () => {
        resetAddingPhase()
        setShowAddPhase(true)
    }

    const addPhaseTitle = editingPhaseId ? 'Edit Phase' : 'Add Phase'
    const addPhaseButtonText = editingPhaseId ? 'Save' : 'Add'
    const phaseFormKey = editingPhaseId ?? 'new'
    const renderExecutableSelect = () => {
        switch (addingPhase.executableType?.value) {
            case 1:
                return <CycleTemplateType formKey={phaseFormKey} addingPhase={addingPhase as IPhaseState<ITaskCycleBindingConfigState>} setAddingPhase={setAddingPhase} />
            case 2:
                return <WorkflowTemplateType formKey={phaseFormKey} addingPhase={addingPhase as IPhaseState<IWorkflowBindingConfigState>} setAddingPhase={setAddingPhase} />
            case 3:
                return <ProcessTemplateType formKey={phaseFormKey} addingPhase={addingPhase as IPhaseState<IProcessBindingConfigState>} setAddingPhase={setAddingPhase} />
        }
    }

    return <>
        <Container>
            <ContainerHeader>
                <TitleText>Phases</TitleText>
            </ContainerHeader>
            <ContainerContent>
                {state.phases.length > 0 ? <HonestTable
                    columns={columns}
                    data={state.phases}
                    keyField='id'
                    onClick={handleEditPhase}
                /> : <NotYetAdded><BodyText>No phases added yet</BodyText></NotYetAdded>}
                {!showAddPhase ? <Button mt='16' orangeBorder onClick={handleOpenAddPhase} >Add Phase</Button> : <AddPhaseSection>
                    <Flex between width='100%' mb='16' >
                        <TitleText weight='bold' >{addPhaseTitle}</TitleText>
                        <CloseIcon onClick={handleCloseAddPhase} />
                    </Flex>
                    <Flex gap='1rem' mb='16'>
                        <Input placeholder='Enter phase name...' labelText='Name' name='name' value={addingPhase.name} onChange={handleChange} nomarg required invalid={!addingPhase.name} />
                        <Input placeholder='Enter sequence' labelText='Sequence' name='sequence' value={addingPhase.sequence} onChange={handleChange} nomarg required invalid={!addingPhase.sequence} type='number' />
                        <HonestSelectInput
                            key={`executableType-${editingPhaseId ?? 'new'}`}
                            options={executableTypeList}
                            labelText='Executable type'
                            name='executableType'
                            onChange={handleSelect}
                            defaultValue={addingPhase.executableType?.value}
                            invalid={!addingPhase.executableType?.value}
                            required
                        />
                        <HonestSelectInput
                            key={`advanceRule-${editingPhaseId ?? 'new'}`}
                            options={advanceRuleList}
                            labelText='Advance rule'
                            name='advanceRule'
                            onChange={handleSelect}
                            defaultValue={addingPhase.advanceRule?.value}
                            invalid={!addingPhase.advanceRule?.value}
                            required
                        />
                    </Flex>
                    {renderExecutableSelect()}
                    <Flex gap='1rem' mt='16' width='100%' justend>
                        <Button orange onClick={handleAddPhase} >{addPhaseButtonText}</Button>
                    </Flex>

                </AddPhaseSection>}
                <SandboxPreview>
                    <BodyText weight='bold' >Sandbox Preview</BodyText>
                    <Flex width='100%' mt='16'>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >CYCLE</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 1).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >WORKFLOW</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 2).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >PROCESS</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 3).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >AUTO</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.advanceRule?.value === 1).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >MANUAL GATE</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.advanceRule?.value === 2).length}</BodyText>
                        </Flex>
                    </Flex>
                </SandboxPreview>

            </ContainerContent>

        </Container>
        <Flex gap='1rem' mt='32' justend>
            <Button onClick={handleBack}>Back</Button>
            <Button orange onClick={handleNext} >Next</Button>
        </Flex>
    </>
}

